
import { SimpleGrid, Loader, Accordion, Badge, Alert, Button } from '@mantine/core';
import { useContext, useEffect, useState } from 'react';

import { UploadBox } from './upload_box';
import { DocTypes, FileCard } from './file_card';
import getKycFiles from '../helpers/getKycFiles';
import { uploadToStorage } from '../helpers/uploadToStorage';
import { CustomClaims, DocType, KYC } from '../types';
import { MyUserDataContext } from '../contexts';
import { IconFileTime, IconQuestionMark } from '@tabler/icons-react';

import {
    QuerySnapshot, DocumentData
} from "firebase/firestore";

const computeShouldShowCard = (linkData: any, currentKycId: string | undefined): boolean => {
    const linkDataMeta = linkData.metadata?.customMetadata;
    const kycId = linkDataMeta?.kycID;
    const kycIdArray = (kycId || "")
        .split(",")
        .map((id: string) => id.trim())
        .filter(Boolean);
    return kycId == null || kycIdArray.includes(currentKycId);
};


type FilesListProps = {
    path: string,
    setMetaTypes?: React.Dispatch<React.SetStateAction<DocType[]>>,
    links: any[],
    kycData?: KYC,
    kycDocs?: QuerySnapshot<DocumentData, DocumentData> | undefined
    forceRefresh?: number | null
    // triggerRefresh: () => void
    setLinks: (links: any[]) => void  //this is to inform whoever is calling us that we have new links.
    onChange?: () => void,
    customDocTypes?: Record<string, DocType>,
    showUploader?: boolean,
    disableDelete?: boolean,
}

export function FilesList(props: FilesListProps) {

    const { path, disableDelete, forceRefresh } = props;
    const userData = useContext(MyUserDataContext);
    const userClaims = userData.firebaseTokenResult?.claims.customClaims as CustomClaims | undefined;
    const isAdmin = userClaims?.admin === true;


    const [links, setLinks] = useState<any[]>(props.links);
    const [visibleLinks, setVisibleLinks] = useState<any[]>([]);
    const [hiddenLinks, setHiddenLinks] = useState<any[]>([]);

    const [uploading, setUploading] = useState(false);


    const [triggerRefresh, setTriggerRefresh] = useState(false);

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const availableDocTypes: Record<string, DocType> = {
        ...DocTypes,
        unreadable: {
            icon: IconQuestionMark,
            name: "Unreadable",
            color: "red",
            key: "unreadable"
        },
        hasExpiryDate: {
            icon: IconFileTime,
            name: "Has Expiry Date",
            key: "hasExpiryDate",
            color: "gray"
        },
    }
    useEffect(() => {
        if (props.setMetaTypes) {
            let docTypes: DocType[] = []
            visibleLinks?.forEach((linkData) => {

                let meta = linkData.metadata?.customMetadata;
                for (let key in meta) {
                    if (!availableDocTypes[key]) {
                        console.log("document type is unknown", key);
                    }
                    else {
                        docTypes.push(availableDocTypes[key]);
                    }
                }
            })

            docTypes = [
                ...new Map(docTypes.map((item: any) => [item?.key, item])).values(),
            ];

            //We need to remove all the duplicate crap from the metatypes after merging.
            props.setMetaTypes((oldDocTypes) => {
                const mergedArray = [...oldDocTypes, ...docTypes];
                const uniqueArray = mergedArray.filter((docType, index, self) =>
                    index === self.findIndex((t) => t.key === docType.key)
                );
                return uniqueArray;
            });

            console.log("Meta types is: ", docTypes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleLinks])



    useEffect(() => {
        let active = true;
        const getData = async () => {
            setLoading(true);
            setLoadError(null);
            setLinks([]);
            try {
                const linkData = (await getKycFiles(path)).filter((x: any) => x !== null);
                if (!active) return;
                setLinks(linkData);
                props.setLinks(linkData);
            } catch (error: any) {
                if (!active) return;
                props.setLinks([]);
                setLoadError(error?.code === 'storage/unauthorized'
                    ? 'You do not have permission to load these files. Please contact an administrator.'
                    : 'Files could not be loaded. Please try again.');
            } finally {
                if (active) setLoading(false);
            }
        }
        getData()
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerRefresh, path, forceRefresh]);

    useEffect(() => {
        const initialVisibleLinks: any[] = [];
        const initialHiddenLinks: any[] = [];
        links.forEach((linkData: any) => {
            // Admins can inspect fund confirmation letters here; other users
            // must not see them in either the files grid or Hidden Files.
            if (!isAdmin && linkData.metadata?.customMetadata?.docType === "fund_confirmation_letter") return;
            const shouldShowCard = computeShouldShowCard(linkData, props.kycData?.id);

            if (shouldShowCard) {
                initialVisibleLinks.push(linkData);
            } else {
                initialHiddenLinks.push(linkData);
            }
        });
        setVisibleLinks(initialVisibleLinks);
        setHiddenLinks(initialHiddenLinks);
    }, [links, props.kycData?.id, isAdmin]);


    return <>
        {loading && <Loader />}
        {loadError && <Alert color="red" title="Unable to load files" role="alert">
            {loadError}
            <Button mt="sm" size="xs" variant="light" onClick={() => setTriggerRefresh(value => !value)}>
                Retry
            </Button>
        </Alert>}
        <SimpleGrid cols={4}>

            {visibleLinks.map((linkData: any) => {
                /// FileCard is the component that shows the file and its metadata.
                return (
                    <FileCard
                        disableDelete={disableDelete}
                        key={linkData.name}
                        linkData={linkData}
                        kycDocs={props.kycDocs}
                        triggerRefresh={() => setTriggerRefresh(value => !value)}
                        onChange={props.onChange}
                    />
                );
            })}

            {(props.showUploader ?? true) && <UploadBox loading={uploading} kycData={props.kycData} kycDocs={props.kycDocs}

                onDropWithTag={async (files, tags) => {
                    setUploading(true);

                    // console.log(files);
                    // const listRef = ref(storage, path);

                    //upload to firebase storage.
                    const promises = files.map(async (file) => {
                        uploadToStorage(file, path, tags);
                    });

                    await Promise.all(promises);

                    // const messaging = getMessaging(getApp());

                    //wait 6 seconds.  I can't know when the thumbnail is ready.
                    await new Promise((resolve) => {
                        setTimeout(() => {
                            resolve(true);
                        }, 6000);
                    });

                    //refreshes listing
                    setUploading(false);
                    setTriggerRefresh(value => !value)

                    if (props.onChange) props.onChange();



                }}
                customDocTypes={props.customDocTypes}
            />}

        </SimpleGrid>
        {hiddenLinks.length > 0 &&
            <Accordion
                defaultValue=""
                variant='contained' transitionDuration={1000}
            >
                <Accordion.Item key='hidden' value='hidden'>
                    <Accordion.Control  >
                        Hidden Files <Badge color='gray' variant='filled' size='md' ml={5} >
                            {hiddenLinks.length}
                        </Badge>
                    </Accordion.Control>
                    <Accordion.Panel>
                        <SimpleGrid cols={4}>
                            {hiddenLinks.map((linkData: any) => {
                                /// FileCard is the component that shows the file and its metadata.
                                return (
                                    <FileCard
                                        disableDelete={disableDelete}
                                        key={linkData.name}
                                        linkData={linkData}
                                        kycDocs={props.kycDocs}
                                        triggerRefresh={() => setTriggerRefresh(value => !value)}
                                        onChange={props.onChange}
                                    />
                                );
                            })}

                        </SimpleGrid>
                    </Accordion.Panel>
                </Accordion.Item>
            </Accordion>

        }
        {/* <p>Note: All files must be tagged</p> */}
    </>


}
